import React, { useEffect, useCallback, useRef, useState, RefObject } from 'react';
import { useInView } from 'react-intersection-observer';

import ButtonLink from 'components/button-link';
import Image from 'components/image';
import PlayButton from './play-button';
import PauseButton from './pause-button';
import VolumeButton from './volume-button';
import './styles.scss';

interface IVideo {
  url: string;
  poster?: string;
  autoPlay?: boolean;
  autoPlayInView?: boolean;
  autoRepeat?: boolean;
  hideVolumeControls?: boolean;
  muted?: boolean;
  skipObserver?: boolean;
  withControls?: boolean;
  forceClick?: boolean;
  transparentPoster?: boolean;
  inViewThreshold?: number;
  handleClick?: () => void;
}

const NOT_ALLOWED_ERROR = 'NotAllowedError';
const TRANSPARENT_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const toggleControlBtn = (btnRef: RefObject<HTMLButtonElement>, show: boolean) => {
  if (btnRef.current) {
    btnRef.current.style.display = show ? 'block' : 'none';
  }
};

const Video: React.FC<IVideo> = ({
  url,
  poster,
  autoPlay = false,
  autoPlayInView = false,
  autoRepeat = false,
  muted = false,
  skipObserver = false,
  withControls= true,
  forceClick = false,
  transparentPoster,
  inViewThreshold,
  hideVolumeControls = false,
  handleClick,
}) => {
  const videoRef = useRef<HTMLVideoElement>();
  const retryAttempts = useRef<number>(1);
  const sourceRef = useRef<HTMLSourceElement>(null);
  const playBtnRef = useRef<HTMLButtonElement>(null);
  const pauseBtnRef = useRef<HTMLButtonElement>(null);
  const volumeBtnRef = useRef<HTMLButtonElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState<boolean>(muted);
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [forcePlayBtn, setForcePlayBtn] = useState<boolean>(false);
  const { ref: inViewRef } = useInView({
    threshold: inViewThreshold || 0.25,
    skip: skipObserver,
    onChange: (inView) => {
      if (inView && autoPlayInView) {
        handlePlay();
      } else {
        handleStop();
      }
    },
  });

  const setVideoRefs = useCallback((node: HTMLVideoElement) => {
    videoRef.current = node;
    inViewRef(node);
  }, [inViewRef]);

  useEffect(() => {
    const source = sourceRef.current;

    const handleVideoFinish = async () => {
      if (autoRepeat) {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;

          await videoRef.current?.play();

          videoContainerRef.current?.classList.add('playing');
        }
      } else {
        if (playBtnRef.current) {
          toggleControlBtn(playBtnRef, true);
          toggleControlBtn(pauseBtnRef, false);
          toggleControlBtn(volumeBtnRef, false);

          setIsFinished(true);
        }
      }
    };

    const handleVideoStarted = () => {
      // We should check if the video has started only once. It's needed to handle the case with autoplay and low
      // battery on iOS devices.
      if (!isStarted) {
        setIsStarted(true);
      }
    };

    const retrySourceLoad = () => {
      if (retryAttempts.current > 0) {
        // Need to create a new source with the same URL to retry load.
        const newSource = document.createElement('source');

        newSource.src = url;
        videoRef.current?.append(newSource);

        retryAttempts.current -= 1;
      }
    };

    if (autoPlay) {
      toggleControlBtn(playBtnRef, false);
    } else {
      toggleControlBtn(pauseBtnRef, false);
      toggleControlBtn(volumeBtnRef, false);
    }

    source?.addEventListener('error', retrySourceLoad);
    videoRef.current?.addEventListener('ended', handleVideoFinish);
    videoRef.current?.addEventListener('playing', handleVideoStarted);

    return () => {
      source?.removeEventListener('error', retrySourceLoad);
      videoRef.current?.removeEventListener('ended', handleVideoFinish);
      videoRef.current?.removeEventListener('playing', handleVideoStarted);
    }
    // eslint-disable-next-line
  }, [url, autoPlay, autoRepeat]);

  const handlePlay = useCallback(async () => {
    try {
      // If the video was finished then reset current time to 0 and start it again.
      if (isFinished && videoRef.current) {
        videoRef.current.currentTime = 0;
      }

      await videoRef.current?.play();

      toggleControlBtn(playBtnRef, false);
      toggleControlBtn(pauseBtnRef, true);
      toggleControlBtn(volumeBtnRef, true);

      setIsFinished(false);
      setForcePlayBtn(false);

      videoContainerRef.current?.classList.add('playing');
    } catch (e: any) {
      // If the device is in the low power mode on iOS then video is suspended and play action will get NOT_ALLOWED_ERROR.
      // Therefore, we show play control for a user to manually start video.
      if (e.name === NOT_ALLOWED_ERROR) {
        setForcePlayBtn(true);
      }
    }
  }, [isFinished]);

  const handleStop = useCallback(async () => {
    try {
      await videoRef.current?.pause();

      toggleControlBtn(playBtnRef, true);
      toggleControlBtn(pauseBtnRef, false);
      toggleControlBtn(volumeBtnRef, false);

      videoContainerRef.current?.classList.remove('playing');
    } catch (e) {}
  }, []);

  const handleVolumeChange = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }

    setIsMuted(!isMuted);
  }, [isMuted]);

  return (
    <div className="video-container flex" ref={videoContainerRef}>
      {(withControls || forcePlayBtn) && (
        <PlayButton innerRef={playBtnRef} onClick={handlePlay} />
      )}
      {withControls && (
        <>
          <PauseButton innerRef={pauseBtnRef} onClick={handleStop} />
          {!hideVolumeControls && (
            <VolumeButton innerRef={volumeBtnRef} isMuted={isMuted} onClick={handleVolumeChange} />
          )}
        </>
      )}
      {((handleClick && isStarted) || forceClick) && (
        <ButtonLink className="main-btn w-100" onClick={handleClick} />
      )}
      <video
        playsInline
        muted={muted}
        autoPlay={autoPlay}
        controls={false}
        preload={autoPlay ? 'auto' : 'none'}
        poster={!poster && transparentPoster ? TRANSPARENT_POSTER : undefined}
        ref={setVideoRefs}
        className="w-100"
      >
        <source ref={sourceRef} src={poster ? url : `${url}#t=0.1`} />
      </video>
      {(poster && !isStarted) && <Image className="poster-image w-100" src={poster} />}
    </div>
  );
};

export default Video;
